// eslint-disable-next-line no-shadow
import { track } from '@eventbrite/datalayer-library';
import { setWindowLocation } from '@eventbrite/http';
import { gettext } from '@eventbrite/i18n';
import { deepKeysToCamel } from '@eventbrite/transformation-utils';
import { $FixMe } from '@eventbrite/ts-utils';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import replace from 'lodash/replace';
import size from 'lodash/size';
import sortBy from 'lodash/sortBy';
import startsWith from 'lodash/startsWith';
import uniq from 'lodash/uniq';
import {
    GA_HOME_CATEGORY,
    GA_HOME_FEED_CATEGORY,
    GA_ONBOARDING_START_ACTION,
    GA_SEE_MORE_ACTION,
    GA_SWITCH_TAB,
    GA_VIEW_EVENT_ACTION,
    USER_VIEWS_HISTORY,
} from '../../../../constants/analytics';
import {
    SMALL_CITY_FALLBACK,
    UPDATE_FEATURED_EVENTS,
} from '../../../../constants/constants';
import {
    ALL_TAB,
    FOR_YOU,
    SEASONAL_TAB,
    TABS_CONFIG,
} from '../../../../constants/tabConfig';
import {
    BucketsContent,
    EntityContext,
    Event,
    Events,
    FeatureFlags,
    Location,
    OrganizerProfile,
    OrganizerProfiles,
} from '../../../../types/index';
import { buildLocationData } from '../../../../utils/location';
import { HEAP_BUCKET_ONLOAD } from '../../constants/analytics';
import {
    getIcon,
    retrieveHomeFeedState,
    transformBucketsContent,
    transformSearchContent,
} from '../../utils';
import {
    updateContent,
    updateTabKey,
    verifyPoorContent,
} from '../content/actions';
import {
    fetchCityBrowseEvents as fetchCityBrowseEventsApi,
    getUserPersonalizedContent,
    getUserSavedEvents,
    searchEventsBy,
    searchEventsWithParameters,
    searchFeaturePaidEvents,
} from './api';
import {
    setCityBrowseCallAsFailed,
    setCityBrowseCallAsOngoing,
    setCityBrowseCallAsSuccess,
} from './cityBrowseActions';
import {
    setSearchCallAsFailed,
    setSearchCallAsOngoing,
    setSearchCallAsSuccess,
} from './searchActions';

import {
    setFeaturedEventsCallAsFailed,
    setFeaturedEventsCallAsOngoing,
    setFeaturedEventsCallAsSuccess,
} from './featuredEventsActions';

let bucketsTrackedByHeap: string[] = [];

const trackViewEvent = ({
    bucketKey,
    eventId,
    tabKey,
    organizerFollowerCount,
    loggedIn = true,
}: {
    bucketKey: number;
    eventId: string;
    tabKey: number;
    organizerFollowerCount: number;
    loggedIn?: boolean;
}) => ({
    type: 'VIEW_EVENT',
    meta: {
        analytics: {
            category: loggedIn ? GA_HOME_FEED_CATEGORY : GA_HOME_CATEGORY,
            action: GA_VIEW_EVENT_ACTION,
            label: replace(`${bucketKey}/${tabKey}`, ' ', ''),
            dimensions: {
                eventId,
            },
            metrics: {
                organizerFollowerCount,
            },
        },
    },
});

export const trackTabChange = ({
    tabKey,
    loggedIn = true,
}: {
    tabKey: string;
    loggedIn?: boolean;
}) => ({
    type: 'CHANGE_TAB',
    meta: {
        analytics: {
            category: loggedIn ? GA_HOME_FEED_CATEGORY : GA_HOME_CATEGORY,
            action: GA_SWITCH_TAB,
            label: tabKey,
        },
    },
});

export const clickOnViewEvent =
    (eventId: string, bucketKey: number, organizerFollowerCount: number) =>
    (dispatch: Function, getState: Function) => {
        const {
            content: { browseState },
            user: { isAuthenticated },
        } = getState();

        const tabKeyGA = startsWith(browseState.tabKey, SEASONAL_TAB)
            ? browseState.tabKeyLabel
            : browseState.tabKey;

        dispatch(
            trackViewEvent({
                bucketKey,
                eventId,
                tabKey: tabKeyGA,
                loggedIn: isAuthenticated,
                organizerFollowerCount,
            }),
        );
    };

const trackSeeMore = ({
    bucketKey,
    tabKey,
    loggedIn = true,
}: {
    bucketKey: number;
    tabKey: number;
    loggedIn: boolean;
}) => ({
    type: 'SEE_MORE_ACTION',
    meta: {
        analytics: {
            category: loggedIn ? GA_HOME_FEED_CATEGORY : GA_HOME_CATEGORY,
            action: GA_SEE_MORE_ACTION,
            label: `${bucketKey}/${tabKey}`,
        },
    },
});

export const clickOnSeeMore =
    (url: string, bucketKey: number) =>
    (dispatch: Function, getState: Function) => {
        const state = getState();
        const {
            user: { isAuthenticated },
            content: {
                browseState: { tabKeyLabel },
            },
        } = state;

        dispatch(
            trackSeeMore({
                bucketKey,
                tabKey: tabKeyLabel,
                loggedIn: isAuthenticated,
            }),
        );

        setWindowLocation(url);
    };

export const UPDATE_EVENT_SAVED_BY_YOU = 'UPDATE_EVENT_SAVED_BY_YOU';

export const UPDATE_LIKES_EVENT_BUCKET = 'UPDATE_LIKES_EVENT_BUCKET';

const finishFetchCityBrowseEvents = (
    dispatch: Function,
    entities: $FixMe,
    entityContext: EntityContext[],
    fetchSuccess = true,
) => {
    dispatch(
        updateContent({
            entities,
            entityContext,
        }),
    );

    if (fetchSuccess) {
        dispatch(setCityBrowseCallAsSuccess());
    } else {
        dispatch(setCityBrowseCallAsFailed());
    }

    // We should enable this later when we use CityBrowse for more things.
    // dispatch(verifyPoorContent({buckets: entityContext, ActionName: 'search_location_change'}));
};

export const fetchCityBrowseEvents =
    ({ locationData }: { locationData?: Location } = {}) =>
    (dispatch: Function, getState: Function) => {
        const { location } = getState();

        dispatch(setCityBrowseCallAsOngoing());

        const _locationData = isEmpty(locationData) ? location : locationData;
        const { placeId, isOnline } = _locationData;

        if (placeId && !isOnline) {
            return fetchCityBrowseEventsApi(placeId)
                .then(({ buckets }: { buckets: BucketsContent }) => {
                    let { entities, entityContext } =
                        transformBucketsContent(buckets);

                    const smallCityFallbackBucket: $FixMe = find(
                        entityContext,
                        {
                            key: SMALL_CITY_FALLBACK,
                        },
                    );

                    // When small_city_fallback, fetch more using the search endpoint events and append them to the same bucket
                    if (
                        smallCityFallbackBucket &&
                        size(smallCityFallbackBucket.events) < 32
                    ) {
                        return searchEventsBy(placeId)
                            .then((response: any) => {
                                const { entities: newEntities, ids } =
                                    transformSearchContent(response);

                                entities = {
                                    ...entities,
                                    ...newEntities,
                                };

                                entityContext = filter(
                                    entityContext,
                                    ({ key }) => key !== SMALL_CITY_FALLBACK,
                                );
                                entityContext = [
                                    ...entityContext,
                                    {
                                        ...smallCityFallbackBucket,
                                        events: uniq([
                                            ...ids,
                                            ...smallCityFallbackBucket.events,
                                        ]),
                                    },
                                ];

                                finishFetchCityBrowseEvents(
                                    dispatch,
                                    entities,
                                    entityContext,
                                );
                            })
                            .catch(() => {
                                dispatch(setCityBrowseCallAsFailed());
                                return [];
                            });
                    }

                    return finishFetchCityBrowseEvents(
                        dispatch,
                        entities,
                        entityContext,
                    );
                })
                .catch(() => {
                    dispatch(setCityBrowseCallAsFailed());
                    return [];
                });
        }

        const { entities, entityContext } = transformBucketsContent([]);

        return finishFetchCityBrowseEvents(
            dispatch,
            entities,
            entityContext,
            false,
        );
    };

export const UPDATE_TAB_CONTENT = 'UPDATE_TAB_CONTENT';

export const buildEventSearch = ({
    flatBucket,
    tabKey,
}: {
    flatBucket?: { results: Events };
    tabKey: string;
}) => {
    const { eventSearch } = TABS_CONFIG[tabKey];

    // eventSearch is empty when is called from switch location
    const _eventSearch = isEmpty(eventSearch)
        ? get(flatBucket, 'eventSearch', { dates: ['current_future'] })
        : eventSearch;

    if (
        tabKey === SEASONAL_TAB &&
        !isEmpty(_eventSearch) &&
        !isString(_eventSearch.q)
    ) {
        _eventSearch.q = _eventSearch.q ? _eventSearch.q.toString() : '';
    }

    return _eventSearch;
};

const getAndUpdateTabKeyLabel =
    ({ tabKey }: { tabKey: string }) =>
    (dispatch: Function, getState: Function) => {
        const {
            user: { isAuthenticated },
            content: { seasonal } = {},
        }: {
            user: { isAuthenticated: boolean };
            content: { seasonal?: any };
        } = getState();

        let { tabKeyLabel } = TABS_CONFIG[tabKey];
        let contentLabel = tabKeyLabel;

        // get actual TabKey to check if is a real tab change or it is tab redirected from logged in/out
        const actualTabKey = retrieveHomeFeedState({
            seasonalContent: seasonal,
            isAuthenticated,
        }).tabKey;

        if (actualTabKey !== tabKey) {
            if (tabKey === SEASONAL_TAB) {
                const seasonalTabLabel = get(
                    seasonal,
                    'byDate.key',
                    get(seasonal, 'byDate.tabName', ''),
                );
                tabKeyLabel = seasonalTabLabel ? seasonalTabLabel : tabKeyLabel;
            }

            dispatch(
                trackTabChange({
                    loggedIn: isAuthenticated,
                    tabKey: replace(tabKeyLabel, ' ', ''),
                }),
            );
            dispatch(updateTabKey({ tabKey, tabKeyLabel }));
            contentLabel = `on_tab_change-${tabKeyLabel}`;
        }

        return contentLabel;
    };

const fetchEventsForRegularTab =
    ({
        locationData,
        tabKey = ALL_TAB,
        shouldUpdateContent,
    }: {
        locationData: Location;
        tabKey: string;
        shouldUpdateContent: boolean;
    }) =>
    (dispatch: Function, getState: Function) => {
        dispatch(setSearchCallAsOngoing());

        const {
            app: { featureFlags },
            location,
            content: { flatBucket } = {},
        }: {
            app: { featureFlags: FeatureFlags };
            location: Location;
            content: { flatBucket?: { results: Events } };
        } = getState();

        const _locationData = buildLocationData({ location, locationData });
        const { placeId, bbox, isOnline } = _locationData;

        const _eventSearch = buildEventSearch({
            flatBucket,
            tabKey,
        });
        const poorContentLabel = dispatch(getAndUpdateTabKeyLabel({ tabKey }));

        /**
         * In some cases, the placeId is not present and also there are no events in the bucket.
         * This happens in smaller cities or BBox places.
         * The general idea is to try another request directly in the frontend to ensure we DO NOT
         * have any events to show.
         * Ideally the backend will solve this soon and we should remove this condition when that happens:
         * https://jira.evbhome.com/browse/EB-129013
         */
        const shouldReRequest =
            !placeId && flatBucket && isEmpty(flatBucket.results);

        if (shouldUpdateContent || shouldReRequest) {
            if (
                !featureFlags.disableCityBrowseOnFeed &&
                tabKey === ALL_TAB &&
                featureFlags.launchEditorsPickInFeed
            ) {
                dispatch(
                    fetchCityBrowseEvents({ locationData: _locationData }),
                );
            }
            dispatch(
                fetchFeaturedPaidEvents({
                    placeId: placeId || '',
                    isFlagEnable: featureFlags.enableFeaturedEvents || false,
                }),
            );
            return searchEventsWithParameters({
                placeId: placeId || '',
                bbox: bbox || {},
                eventSearch: _eventSearch,
                isOnline: isOnline || false,
            })
                .then(({ events }: any) => {
                    const allEvents = [
                        ...events.promoted_results,
                        ...events.results,
                    ];
                    dispatch({
                        type: UPDATE_TAB_CONTENT,
                        payload: {
                            events: allEvents,
                            eventSearch: _eventSearch,
                        },
                    });
                    dispatch(
                        verifyPoorContent({
                            events: allEvents,
                            actionName: poorContentLabel,
                        }),
                    );

                    dispatch(setSearchCallAsSuccess());
                })
                .catch(() => {
                    dispatch(setSearchCallAsFailed());
                });
        }

        dispatch(
            verifyPoorContent({
                events: _eventSearch,
                actionName: poorContentLabel,
            }),
        );

        return dispatch(setSearchCallAsFailed());
    };

/**
 * @name fetchEventsForYouTab
 * @param { tabKey }: an object containing a string representation of the tabKey
 * @description fetches recommended events from the personalized user content api
 */
const fetchEventsForYouTab =
    ({ tabKey, filters }: { tabKey: string; filters?: Array<object> }) =>
    async (dispatch: Function, getState: Function) => {
        dispatch(setSearchCallAsOngoing());

        const {
            user: { isAuthenticated },
            location,
            app: { locale },
        }: {
            user: { isAuthenticated: boolean };
            location: Location;
            app: { locale: string };
        } = getState();

        dispatch(getAndUpdateTabKeyLabel({ tabKey }));

        if (!isAuthenticated) {
            dispatch(setSearchCallAsSuccess());
            return dispatch({
                type: UPDATE_TAB_CONTENT,
                payload: {
                    events: [],
                },
            });
        }

        const entityContext: { [key: string]: EntityContext } = {};

        const searches =
            typeof window !== 'undefined'
                ? window.localStorage.getItem('search:autocomplete:history')
                : '';

        const formattedPreviousSearches =
            searches
                ?.split(',')
                ?.map((query: string) => query.trim().toLowerCase())
                ?.filter((trimmedQ: string) => !!trimmedQ)
                ?.slice(0, 5) || [];

        const views =
            typeof window !== 'undefined'
                ? window.localStorage.getItem(USER_VIEWS_HISTORY)
                : '';

        const updatedFilters = [
            ...(Array.isArray(filters) ? filters : []),
            { name: 'place_id', value: location.placeId, payload: 'data' },
            {
                name: 'user_recent_searches',
                value: formattedPreviousSearches,
                payload: 'data',
            },
            { name: 'user_recent_views', value: views, payload: 'data' },
            { name: 'logged_in', value: isAuthenticated, payload: 'data' },
        ];
        const recommendedEvents = await getUserPersonalizedContent(
            locale,
            updatedFilters,
        ).catch(() => {
            return null;
        });

        const buckets = recommendedEvents?.buckets
            ? recommendedEvents.buckets.map(deepKeysToCamel)
            : [];

        const entities: { [key: string]: Event } = {};

        if (!isEmpty(buckets)) {
            buckets.forEach(
                (bucket: {
                    key: string;
                    type: string;
                    name: string;
                    events?: Events;
                    profiles?: OrganizerProfiles;
                }) => {
                    if (bucket.events) {
                        entityContext[bucket.key] = {
                            key: bucket.key,
                            type: bucket.type,
                            name: bucket.name,
                            events: bucket.events.map(
                                (event: Event) => event.id,
                            ),
                            icon: getIcon(tabKey, bucket.type),
                        };

                        bucket.events.forEach((event: Event) => {
                            entities[event.id || ''] = event;
                        });
                    }

                    if (bucket.profiles) {
                        entityContext[bucket.key] = {
                            key: bucket.key,
                            type: bucket.type,
                            name: bucket.name,
                            profiles: bucket.profiles.map(
                                (profile: OrganizerProfile) => profile.id,
                            ),
                            icon: getIcon(tabKey, bucket.type),
                        };

                        bucket.profiles.forEach((profile: OrganizerProfile) => {
                            entities[profile.id || ''] = profile;
                        });
                    }

                    // only track a bucket load once
                    const isBucketTrackedByHeap = bucketsTrackedByHeap.find(
                        (bucketTracked: string) =>
                            bucket.name === bucketTracked,
                    );

                    if (
                        bucket.name &&
                        isAuthenticated &&
                        !isBucketTrackedByHeap
                    ) {
                        track({
                            eventName: HEAP_BUCKET_ONLOAD,
                            eventData: {
                                personalizationBucketName: bucket.name,
                                personalizationBucketKey: bucket.key,
                            },
                        });

                        bucketsTrackedByHeap = [
                            ...bucketsTrackedByHeap,
                            bucket.name,
                        ];
                    }
                },
            );

            dispatch(
                updateContent({
                    entityContext: Object.values(entityContext),
                    entities,
                }),
            );
        }

        dispatch(setSearchCallAsSuccess());
    };

/**
 * @name fetchUserSavedEvents
 * @description fetches saved/liked events for a given user that are sorted and returned
 * - Runs when the containing bucket component enters the viewport
 * @returns Event[] objects to be displayed in the home page For You tab
 */
export const fetchUserSavedEvents =
    () => async (dispatch: Function, getState: Function) => {
        const {
            user: { isAuthenticated },
            content: { entityContext },
        }: {
            user: { isAuthenticated: boolean };
            content: {
                entityContext: EntityContext[];
            };
        } = getState();
        const entities: { [key: string]: Event } = {};
        const ENTITY_KEY_RECOMMENDED_EVENTS = 'recommended_events';

        const userSavedEvents = isAuthenticated
            ? await getUserSavedEvents()
                  .then((results: any) => {
                      return sortBy(
                          results.events,
                          (event) => event.start_date,
                      ).map(deepKeysToCamel);
                  })
                  .catch(() => {
                      return null;
                  })
            : [];

        if (
            !isEmpty(userSavedEvents) &&
            !entityContext.find(
                (entity) => entity.key === ENTITY_KEY_RECOMMENDED_EVENTS,
            )
        ) {
            userSavedEvents.forEach((event: Event) => {
                entities[event.id || ''] = event;
            });

            entityContext.push({
                key: ENTITY_KEY_RECOMMENDED_EVENTS,
                type: 'event',
                name: gettext('My liked events'),
                events: userSavedEvents.map((event: Event) => event.id),
            });

            dispatch(
                updateContent({
                    entityContext,
                    entities,
                }),
            );
        }

        dispatch(setSearchCallAsSuccess());
    };

// Fetch events from Search endpoint
export const fetchEventsForTab = ({
    locationData,
    tabKey = ALL_TAB,
    shouldUpdateContent,
    filters,
}: {
    locationData?: Location;
    tabKey: string;
    shouldUpdateContent?: boolean;
    filters?: Array<object>;
}) => {
    if (tabKey === FOR_YOU) {
        return fetchEventsForYouTab({
            tabKey,
            filters,
        });
    }

    return fetchEventsForRegularTab({
        locationData: locationData || {},
        tabKey,
        shouldUpdateContent: shouldUpdateContent || false,
    });
};

const trackOnboardingClick = ({ loggedIn = true }) => ({
    type: 'ONBOARDING_CARD_CLICK',
    meta: {
        analytics: {
            category: loggedIn ? GA_HOME_FEED_CATEGORY : GA_HOME_CATEGORY,
            action: GA_ONBOARDING_START_ACTION,
            label: '',
            onWindowUnload: true,
        },
    },
});

export const clickOnOnboardingCard =
    () => (dispatch: Function, getState: Function) => {
        const {
            user: { isAuthenticated },
        } = getState();

        dispatch(
            trackOnboardingClick({
                loggedIn: isAuthenticated,
            }),
        );
    };

export const fetchFeaturedPaidEvents =
    ({ placeId, isFlagEnable }: { placeId: string; isFlagEnable: boolean }) =>
    (dispatch: Function, getState: Function) => {
        const {
            user: { isAuthenticated },
            content: { browseState },
        }: {
            user: { isAuthenticated: boolean };
            content: { browseState: any };
        } = getState();
        if (
            !isAuthenticated &&
            isFlagEnable &&
            browseState.tabKey === ALL_TAB
        ) {
            dispatch(setFeaturedEventsCallAsOngoing());
            return searchFeaturePaidEvents(placeId)
                .then((response: any) => {
                    let filteredData = []; // filter past events which has already completed
                    if (response != undefined) {
                        const currentDateTime = new Date();
                        filteredData = response?.events.filter(
                            (item: any) =>
                                currentDateTime <=
                                    new Date(
                                        `${item.end_date}T${item.end_time}`,
                                    ) && !item.ticket_availability.is_sold_out,
                        );
                    }
                    dispatch({
                        type: UPDATE_FEATURED_EVENTS,
                        payload: {
                            events: filteredData,
                        },
                    });
                    dispatch(setFeaturedEventsCallAsSuccess());
                })
                .catch(() => {
                    dispatch(setFeaturedEventsCallAsFailed);
                });
        }
    };
